import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogBaseExtendProps } from 'components/_new/Dialog';
import { Button } from 'components/_new/Button';
import { Session } from 'types/Session';
import { SessionFile } from 'types/SessionFile';
import { useGlobal } from 'contexts/globalContext';
import { UploadUserFiles, UploadUserFilesRef } from './UploadUserFiles';

type UploadUserFilesDialogProps = DialogBaseExtendProps & {
	session?: Pick<Session, 'id' | 'sessionNumber' | 'pairId'>;
	selectedUserfile?: SessionFile;
	setSelectedUserfile: (file?: SessionFile) => void;
	onFinish: () => void;
	withAddNewButton?: boolean;
};

export const UploadUserFilesDialog = ({
	session,
	selectedUserfile,
	setSelectedUserfile,
	onFinish,
	withAddNewButton = false,
	...dialogProps
}: UploadUserFilesDialogProps) => {
	const { onHide } = dialogProps;

	const uploadUserFilesRef = useRef<UploadUserFilesRef>(null);
	const { t } = useTranslation();
	const [hasChanges, setHasChanges] = useState(false);
	const { setOpenWindows } = useGlobal();

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (hasChanges && dialogProps.visible) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [hasChanges, dialogProps.visible]);

	const handleClose = () => {
		if (hasChanges) {
			// eslint-disable-next-line no-alert
			if (window.confirm(t('userPanel.userFiles.abandonChanges'))) {
				setSelectedUserfile(undefined);
				setHasChanges(false);
				onHide?.();
			}
		} else {
			setSelectedUserfile(undefined);
			onHide?.();
		}
	};

	return (
		<Dialog
			title={selectedUserfile ? t('userPanel.userFiles.modal.edit') : t('userPanel.userFiles.modal.add')}
			{...dialogProps}
			onHide={handleClose}
			dialogSize="lg"
			headerRight={() => (
				<Button
					className="hidden lg:inline-flex"
					label={t('userPanel.userFiles.modal.openNoteInNewWindow')}
					variant="primary-outlined"
					variantSize="sm"
					icon="external-link-alt"
					iconSet="fa"
					iconContainerClassName="mr-2"
					onClick={() => {
						if (session) {
							uploadUserFilesRef?.current?.save().then((data) => {
								const url = `/panel/demo/mentoring/mentee/pair/${session.pairId}/session/${
									session.id
								}/note${data ? `/${data.id}` : ''}`;
								const newWindow = window.open(url, '_blank', 'width=1024,height=720,resizable=no');
								if (newWindow) {
									setOpenWindows((prev) => ({
										...prev,
										ADD_SESSION_NOTE: [...(prev.ADD_SESSION_NOTE || []), { window: newWindow }],
									}));
									onHide?.();
								}
							});
						}
					}}
				/>
			)}
		>
			<UploadUserFiles
				ref={uploadUserFilesRef}
				userFile={selectedUserfile}
				setUserFile={setSelectedUserfile}
				onFinish={() => {
					onFinish?.();
				}}
				onSave={() => {
					setHasChanges(false);
				}}
				onClose={() => {
					onHide?.();
				}}
				setHasChanges={setHasChanges}
				session={session}
				withAddNewButton={withAddNewButton}
			/>
		</Dialog>
	);
};
